import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { viewYoutubeService, ViewYoutube, ViewYoutubeHistory } from '../../services/viewYoutubeService';
import { settingsService } from '../../services/settingsService';
import { useToast } from '../../components/Toast';
import './AdminYouTubeViewPage.css';

const AdminYouTubeViewPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { showToast } = useToast();
  const [videos, setVideos] = useState<ViewYoutube[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [channelOptions, setChannelOptions] = useState<string[]>([]);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(50);

  const [selectedVideos, setSelectedVideos] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState<number | null>(null);
  const [history, setHistory] = useState<ViewYoutubeHistory[]>([]);
  const [expandedLinkIds, setExpandedLinkIds] = useState<Set<number>>(new Set());
  const [expandedTitleIds, setExpandedTitleIds] = useState<Set<number>>(new Set());
  const [checkViewEnabled, setCheckViewEnabled] = useState(true);
  const [loadingSetting, setLoadingSetting] = useState(false);
  const [formData, setFormData] = useState<Partial<ViewYoutube>>({
    Content: '',
    Country: 'Vietnam',
    View: 0,
    NameChannel: '',
    Uploader: '',
    Linkvideo: '',
    Datetimeuploading: new Date().toISOString(),
    status: 'active',
  });

  // Load videos ngay khi vào trang (dùng date range mặc định)
  useEffect(() => {
    if (!dateStart || !dateEnd) {
      // Set default date range (last 30 days)
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      setDateEnd(end.toISOString().split('T')[0]);
      setDateStart(start.toISOString().split('T')[0]);
    }
  }, []);

  // Load videos when date range, status filter, search, channel filter, or page changes
  useEffect(() => {
    if (dateStart && dateEnd) {
      fetchVideos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStart, dateEnd, statusFilter, search, channelFilter, page]);

  const fetchVideos = async () => {
    // Sử dụng daterange endpoint từ doc
    // Nếu không có date range, dùng range rộng (1 năm trước đến hiện tại)
    const start = dateStart || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const end = dateEnd || new Date().toISOString().split('T')[0];

    setLoading(true);
    try {
      const data = await viewYoutubeService.getByDateRange(start, end);

      let filtered = data;

      // Cập nhật danh sách kênh từ toàn bộ dữ liệu trả về (theo date range)
      const uniqueChannels = Array.from(
        new Set(
          data
            .map((v) => v.NameChannel?.trim())
            .filter((name): name is string => !!name)
        )
      ).sort((a, b) => a.localeCompare(b, 'vi'));
      setChannelOptions(uniqueChannels);

      // Filter by status
      if (statusFilter) {
        filtered = filtered.filter(v => v.status === statusFilter);
      }

      // Filter by channel name
      if (channelFilter) {
        const keyword = channelFilter.toLowerCase();
        filtered = filtered.filter(v =>
          (v.NameChannel || '').toLowerCase().includes(keyword)
        );
      }

      // Apply client-side filters for search
      if (search) {
        filtered = filtered.filter(v =>
          v.Content?.toLowerCase().includes(search.toLowerCase()) ||
          v.NameChannel?.toLowerCase().includes(search.toLowerCase()) ||
          v.Linkvideo?.toLowerCase().includes(search.toLowerCase())
        );
      }

      // Pagination
      const startIndex = (page - 1) * perPage;
      const endIndex = startIndex + perPage;
      const paginated = filtered.slice(startIndex, endIndex);

      setVideos(paginated);
      setTotal(filtered.length);
    } catch (error: any) {
      showToast('Lỗi khi tải danh sách video', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newVideo = await viewYoutubeService.upload(formData);
      showToast(
        `Tạo video thành công. View hiện tại: ${formatNumber(newVideo.View || 0)}`,
        'success'
      );
      setShowCreateForm(false);
      resetForm();
      // Cập nhật ngay vào danh sách đang hiển thị để thấy lượt xem mới
      setVideos((prev) => [newVideo, ...prev]);
      setTotal((prev) => prev + 1);
      // Đồng bộ lại dữ liệu theo filter hiện tại
      fetchVideos();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi tạo video', 'error');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    try {
      await viewYoutubeService.updateDetail(editingId, {
        content: formData.Content,
        country: formData.Country,
        view: formData.View,
        nameChannel: formData.NameChannel,
        seeder: formData.Uploader,
        groupLink: formData.Linkvideo,
        date: formData.Datetimeuploading,
      });
      showToast('Cập nhật thành công', 'success');
      setEditingId(null);
      resetForm();
      fetchVideos();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi cập nhật', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bạn có chắc muốn xóa video này?')) return;
    try {
      await viewYoutubeService.delete(id);
      showToast('Xóa thành công', 'success');
      setSelectedVideos(selectedVideos.filter(vId => vId !== id));
      fetchVideos();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi xóa', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedVideos.length === 0) return;
    if (!window.confirm(`Bạn có chắc muốn xóa ${selectedVideos.length} video?`)) return;
    try {
      await viewYoutubeService.deleteBatch(selectedVideos);
      showToast(`Đã xóa ${selectedVideos.length} video`, 'success');
      setSelectedVideos([]);
      setSelectAll(false);
      fetchVideos();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi xóa hàng loạt', 'error');
    }
  };

  const handleBatchUpload = async (links: string[]) => {
    try {
      const videosData = links
        .filter(link => link.trim())
        .map(link => ({
          Content: '',
          Country: 'Vietnam',
          View: 0,
          NameChannel: '',
          Uploader: '',
          Linkvideo: link.trim(),
          Datetimeuploading: new Date().toISOString(),
        }));

      if (videosData.length === 0) {
        showToast('Vui lòng nhập ít nhất 1 link', 'warning');
        return;
      }

      await viewYoutubeService.uploadBatch(videosData);
      showToast(`Đã upload ${videosData.length} video`, 'success');
      fetchVideos();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi upload hàng loạt', 'error');
    }
  };

  const handleViewHistory = async (id: number) => {
    try {
      const historyData = await viewYoutubeService.getHistory(id);
      setHistory(historyData);
      setShowHistory(id);
    } catch (error: any) {
      showToast('Lỗi khi tải lịch sử', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      Content: '',
      Country: 'Vietnam',
      View: 0,
      NameChannel: '',
      Uploader: '',
      Linkvideo: '',
      Datetimeuploading: new Date().toISOString(),
      status: 'active',
    });
    setEditingId(null);
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedVideos(videos.map(v => v.id!));
    } else {
      setSelectedVideos([]);
    }
  };

  const handleSelectVideo = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedVideos([...selectedVideos, id]);
    } else {
      setSelectedVideos(selectedVideos.filter(vId => vId !== id));
      setSelectAll(false);
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('vi-VN').format(num);
  };

  const toggleLinkExpand = (id?: number) => {
    if (!id) return;
    setExpandedLinkIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleTitleExpand = (id?: number) => {
    if (!id) return;
    setExpandedTitleIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderLinkCell = (video: ViewYoutube) => {
    const link = video.Linkvideo || '';
    const isExpanded = video.id ? expandedLinkIds.has(video.id) : false;
    const shouldTruncate = link.length > 60;
    const displayText = !shouldTruncate || isExpanded ? link : `${link.slice(0, 60)}...`;

    return (
      <div className="link-cell-content">
        <a
          href={video.Linkvideo}
          target="_blank"
          rel="noopener noreferrer"
          className={`check-view-link ${isExpanded ? 'expanded' : ''}`}
        >
          {displayText}
        </a>
        {shouldTruncate && (
          <button
            type="button"
            className="inline-action"
            onClick={() => toggleLinkExpand(video.id)}
          >
            {isExpanded ? 'Thu gọn' : 'Xem thêm'}
          </button>
        )}
      </div>
    );
  };

  const renderTitleCell = (video: ViewYoutube) => {
    const title = video.Content || video.NameChannel || '';
    const isExpanded = video.id ? expandedTitleIds.has(video.id) : false;
    const shouldTruncate = title.length > 60;
    const displayText = !shouldTruncate || isExpanded ? title : `${title.slice(0, 60)}...`;

    return (
      <div className="check-view-title with-toggle">
        <span>{displayText || '-'}</span>
        {shouldTruncate && (
          <button
            type="button"
            className="inline-action"
            onClick={() => toggleTitleExpand(video.id)}
          >
            {isExpanded ? 'Thu gọn' : 'Xem thêm'}
          </button>
        )}
      </div>
    );
  };

  // Load check view setting
  useEffect(() => {
    const loadSetting = async () => {
      try {
        const enabled = await settingsService.isCheckViewEnabled();
        setCheckViewEnabled(enabled);
      } catch (error) {
        console.error('Error loading check view setting:', error);
      }
    };
    loadSetting();
  }, []);

  // Load videos on mount
  useEffect(() => {
    fetchVideos();
  }, []);

  const handleToggleCheckView = async () => {
    setLoadingSetting(true);
    try {
      const newValue = !checkViewEnabled;
      await settingsService.setCheckViewEnabled(newValue);
      setCheckViewEnabled(newValue);
      showToast(
        newValue ? 'Đã bật chức năng Check View YouTube' : 'Đã tắt chức năng Check View YouTube',
        'success'
      );
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi cập nhật cài đặt', 'error');
    } finally {
      setLoadingSetting(false);
    }
  };

  return (
    <div className="admin-youtube-view-page">
      <div className="page-header">
        <h1>Quản lý Check View YouTube</h1>
        <div className="header-actions">
          <div className="check-view-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={checkViewEnabled}
                onChange={handleToggleCheckView}
                disabled={loadingSetting}
              />
              <span className="toggle-text">
                {checkViewEnabled ? '✓ Đã bật' : '✗ Đã tắt'} Check View
              </span>
            </label>
          </div>
          {selectedVideos.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="btn btn-danger"
            >
              Xóa đã chọn ({selectedVideos.length})
            </button>
          )}
          <button
            onClick={() => {
              resetForm();
              setShowCreateForm(true);
            }}
            className="btn btn-primary"
          >
            + Thêm mới
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Từ ngày:</label>
          <input
            type="date"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            className="date-input"
          />
        </div>
        <div className="filter-group">
          <label>Đến ngày:</label>
          <input
            type="date"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
            className="date-input"
          />
        </div>
        <button onClick={fetchVideos} className="btn btn-secondary">
          Tải dữ liệu
        </button>
        <input
          type="text"
          placeholder={t('common.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="search-input"
        />
        <select
          value={channelFilter}
          onChange={(e) => {
            setChannelFilter(e.target.value);
            setPage(1);
          }}
          className="filter-select"
        >
          <option value="">Tất cả kênh</option>
          {channelOptions.map((ch) => (
            <option key={ch} value={ch}>{ch}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="filter-select"
        >
          <option value="">Tất cả trạng thái</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
          <option value="deleted">Deleted</option>
        </select>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateForm(false);
          resetForm();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingId ? 'Chỉnh sửa' : 'Thêm mới'} Video</h2>
            <form onSubmit={editingId ? handleUpdate : handleCreate}>
              <div className="form-group">
                <label>Tiêu đề (Content) *</label>
                <input
                  type="text"
                  value={formData.Content || ''}
                  onChange={(e) => setFormData({ ...formData, Content: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Link YouTube *</label>
                <input
                  type="url"
                  value={formData.Linkvideo || ''}
                  onChange={(e) => setFormData({ ...formData, Linkvideo: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                  required
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Quốc gia (Country)</label>
                  <input
                    type="text"
                    value={formData.Country || ''}
                    onChange={(e) => setFormData({ ...formData, Country: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Số view (View)</label>
                  <input
                    type="number"
                    value={formData.View || 0}
                    onChange={(e) => setFormData({ ...formData, View: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>Tên kênh (NameChannel)</label>
                  <input
                    type="text"
                    value={formData.NameChannel || ''}
                    onChange={(e) => setFormData({ ...formData, NameChannel: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Người upload (Uploader)</label>
                  <input
                    type="text"
                    value={formData.Uploader || ''}
                    onChange={(e) => setFormData({ ...formData, Uploader: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Ngày upload</label>
                  <input
                    type="datetime-local"
                    value={formData.Datetimeuploading ? new Date(formData.Datetimeuploading).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setFormData({ ...formData, Datetimeuploading: new Date(e.target.value).toISOString() })}
                  />
                </div>
                <div className="form-group">
                  <label>Trạng thái</label>
                  <select
                    value={formData.status || 'active'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                    <option value="deleted">Deleted</option>
                  </select>
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Cập nhật' : 'Tạo mới'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Hủy
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="modal-overlay" onClick={() => setShowHistory(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Lịch sử thay đổi</h2>
            <div className="history-list">
              {history.length === 0 ? (
                <p>Không có lịch sử thay đổi</p>
              ) : (
                history.map((h) => (
                  <div key={h.id} className="history-item">
                    <div className="history-field">{h.fieldName}</div>
                    <div className="history-change">
                      <span className="old-value">{h.oldValue}</span>
                      <span className="arrow">→</span>
                      <span className="new-value">{h.newValue}</span>
                    </div>
                    <div className="history-meta">
                      Bởi: {h.changedBy} - {h.createdAt ? new Date(h.createdAt).toLocaleString('vi-VN') : ''}
                    </div>
                  </div>
                ))
              )}
            </div>
            <button
              onClick={() => setShowHistory(null)}
              className="btn btn-secondary"
            >
              Đóng
            </button>
          </div>
        </div>
      )}

      {/* Batch Upload Form */}
      <div className="batch-upload-section">
        <h3>Upload nhiều link YouTube</h3>
        <textarea
          placeholder="Nhập các link YouTube, mỗi link một dòng..."
          className="batch-upload-textarea"
          rows={5}
          id="batch-upload-input"
        />
        <button
          onClick={() => {
            const textarea = document.getElementById('batch-upload-input') as HTMLTextAreaElement;
            const links = textarea.value.split('\n').filter(l => l.trim());
            handleBatchUpload(links);
            textarea.value = '';
          }}
          className="btn btn-secondary"
        >
          Upload hàng loạt
        </button>
      </div>

      {/* Videos Table */}
      {loading ? (
        <div className="loading">Đang tải...</div>
      ) : videos.length === 0 ? (
        <div className="empty-state">
          <p>Chưa có video nào trong khoảng thời gian đã chọn. Vui lòng chọn khoảng thời gian khác hoặc thêm video mới.</p>
        </div>
      ) : (
        <div className="table-container">
          {/* Desktop Table View */}
          <div className="table-wrapper">
            <table className="videos-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th>STT</th>
                  <th>Link Video</th>
                  <th>Tiêu đề Video</th>
                  <th>Kênh</th>
                  <th>Người thêm</th>
                  <th>Lượt xem</th>
                  <th>Quốc gia</th>
                  <th>Trạng thái</th>
                  <th>Check lần cuối</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((video, index) => (
                  <tr key={video.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedVideos.includes(video.id!)}
                        onChange={(e) => handleSelectVideo(video.id!, e.target.checked)}
                      />
                    </td>
                    <td>{(page - 1) * perPage + index + 1}</td>
                    <td className="link-cell">{renderLinkCell(video)}</td>
                    <td className="content-cell">{renderTitleCell(video)}</td>
                    <td>{video.NameChannel || '-'}</td>
                    <td>{video.created_by_full_name || video.created_by_username || video.Uploader || '-'}</td>
                    <td className="view-cell">{formatNumber(video.View || 0)}</td>
                    <td>{video.Country || '-'}</td>
                    <td>
                      <span className={`status-badge status-${video.status}`}>
                        {video.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {video.last_checked_at
                        ? new Date(video.last_checked_at).toLocaleString('vi-VN')
                        : '-'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => {
                            setFormData(video);
                            setEditingId(video.id!);
                            setShowCreateForm(true);
                          }}
                          className="btn-icon btn-edit"
                          title="Chỉnh sửa"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleViewHistory(video.id!)}
                          className="btn-icon btn-history"
                          title="Lịch sử"
                        >
                          📜
                        </button>
                        <button
                          onClick={() => handleDelete(video.id!)}
                          className="btn-icon btn-delete"
                          title="Xóa"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="admin-mobile-cards">
            {videos.map((video, index) => (
              <div key={video.id} className="admin-mobile-card">
                <div className="admin-mobile-card-header">
                  <span className="admin-mobile-card-id">#{(page - 1) * perPage + index + 1}</span>
                  <span className={`status-badge status-${video.status}`}>
                    {video.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="admin-mobile-card-body">
                  <div className="admin-mobile-card-item">
                    <span className="admin-mobile-card-label">Link Video:</span>
                    <div className="admin-mobile-card-link-block">
                      {renderLinkCell(video)}
                    </div>
                  </div>
                  <div className="admin-mobile-card-item">
                    <span className="admin-mobile-card-label">Tiêu đề Video:</span>
                    <div className="admin-mobile-card-value">
                      {renderTitleCell(video)}
                    </div>
                  </div>
                  <div className="admin-mobile-card-item">
                    <span className="admin-mobile-card-label">Người thêm:</span>
                    <span className="admin-mobile-card-value">
                      {video.created_by_full_name || video.created_by_username || video.Uploader || '-'}
                    </span>
                  </div>
                  <div className="admin-mobile-card-row">
                    <div className="admin-mobile-card-item">
                      <span className="admin-mobile-card-label">Lượt xem:</span>
                      <span className="admin-mobile-card-value highlight">{formatNumber(video.View || 0)}</span>
                    </div>
                    <div className="admin-mobile-card-item">
                      <span className="admin-mobile-card-label">Quốc gia:</span>
                      <span className="admin-mobile-card-value">{video.Country || '-'}</span>
                    </div>
                  </div>
                  {video.last_checked_at && (
                    <div className="admin-mobile-card-item">
                      <span className="admin-mobile-card-label">Check lần cuối:</span>
                      <span className="admin-mobile-card-value">
                        {new Date(video.last_checked_at).toLocaleString('vi-VN')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="admin-mobile-card-actions">
                  <button
                    onClick={() => {
                      setFormData(video);
                      setEditingId(video.id!);
                      setShowCreateForm(true);
                    }}
                    className="admin-mobile-action-btn edit"
                    title="Chỉnh sửa"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Sửa</span>
                  </button>
                  <button
                    onClick={() => handleViewHistory(video.id!)}
                    className="admin-mobile-action-btn history"
                    title="Lịch sử"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Lịch sử</span>
                  </button>
                  <button
                    onClick={() => handleDelete(video.id!)}
                    className="admin-mobile-action-btn delete"
                    title="Xóa"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Xóa</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {Math.ceil(total / perPage) > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="pagination-btn"
          >
            Trước
          </button>
          <span className="pagination-info">
            Trang {page} / {Math.ceil(total / perPage)} ({total} video)
          </span>
          <button
            onClick={() => setPage(p => Math.min(Math.ceil(total / perPage), p + 1))}
            disabled={page >= Math.ceil(total / perPage)}
            className="pagination-btn"
          >
            Sau
          </button>
        </div>
      )}

      {/* Show total count */}
      {videos.length > 0 && (
        <div className="total-info">
          <p>Tổng: {total} video</p>
        </div>
      )}
    </div>
  );
};

export default AdminYouTubeViewPage;

